import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import "./PaymentSuccess.css";
import { checkAvailability } from "../carAvailability/carAvailabilityThunk";
import ClipLoader from "react-spinners/ClipLoader";
import { saveBooking } from "../booking/bookingsThunk";
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { sendPaymentIssueEmail } from "./paymentIssueNotifier";
import {
  buildBookingConfirmationEmailContent,
  createBookingConfirmationPdf,
} from "../../../utils/bookingConfirmation";
import {
  clearPendingBookingContext,
  readPendingBookingContext,
  storePendingBookingContext,
} from "./pendingBookingStorage";
import {
  setEmail,
  setCellphone,
  setCountry,
  setName,
  setTotalPrice,
} from "../booking/bookingsSlice";
import {
  setDate,
  setSession,
  setPeople,
  setPickupPoint,
} from "../carAvailability/carAvailabilitySlice";

function PaymentSuccess() {
  const location = useLocation();
  const dispatch = useDispatch();
  const [paymentStatus, setPaymentStatus] = useState("");
  const [statusTone, setStatusTone] = useState("info");
  const [hasVerified, setHasVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const nav = useNavigate();
  const bookingDetails = useSelector((state) => state.bookings);
  const carAvailabilityDetails = useSelector((state) => state.carAvailability);
  const bookingFinalizedRef = useRef(false);
  const [pendingBookingId, setPendingBookingId] = useState(null);
  const fetchedPendingIdsRef = useRef(new Set());
  const pendingSnapshotsRef = useRef(new Map());
  const tokenLookupCacheRef = useRef(new Map());
  const pendingResolutionCacheRef = useRef(new Map());
  // Tracks the most recently observed transaction token so we can reuse it
  // across rehydration attempts even if the gateway omits it on later redirects.
  // This must stay as a ref (instead of a memoized callback) so callers can
  // freely read and assign to `.current` while resolving pending bookings.
  const lastTransactionTokenRef = useRef(null);

  const getLastTransactionToken = useCallback(() => {
    return lastTransactionTokenRef.current;
  }, []);

  const setLastTransactionToken = useCallback((token) => {
    lastTransactionTokenRef.current =
      typeof token === "string" ? token.trim() || token : token ?? null;
  }, []);

  const resetLastTransactionToken = useCallback(() => {
    lastTransactionTokenRef.current = null;
  }, []);

  const updateStatus = useCallback((message, tone = "info") => {
    setPaymentStatus(message);
    setStatusTone(tone);
  }, []);

  const hydrateBookingState = useCallback(
    (bookingPayload = {}) => {
      if (typeof bookingPayload.email === "string") {
        dispatch(setEmail(bookingPayload.email));
      }
      if (typeof bookingPayload.cellphone === "string") {
        dispatch(setCellphone(bookingPayload.cellphone));
      }
      if (typeof bookingPayload.country === "string") {
        dispatch(setCountry(bookingPayload.country));
      }
      if (typeof bookingPayload.name === "string") {
        dispatch(setName(bookingPayload.name));
      }
      if (bookingPayload.totalPrice !== undefined) {
        dispatch(setTotalPrice(Number(bookingPayload.totalPrice) || 0));
      }
    },
    [dispatch]
  );

  const hydrateAvailabilityState = useCallback(
    (availabilityPayload = {}) => {
      if (typeof availabilityPayload.date === "string") {
        dispatch(setDate(availabilityPayload.date));
      }
      if (typeof availabilityPayload.session === "string") {
        dispatch(setSession(availabilityPayload.session));
      }
      if (availabilityPayload.people !== undefined) {
        dispatch(setPeople(Number(availabilityPayload.people) || 0));
      }
      if (typeof availabilityPayload.pickupPoint === "string") {
        dispatch(setPickupPoint(availabilityPayload.pickupPoint));
      }
    },
    [dispatch]
  );

  const fetchAndHydratePendingBooking = useCallback(
    async (pendingId, token) => {
      const normalizedPendingId =
        typeof pendingId === "string" ? pendingId.trim() : "";
      const normalizedToken =
        typeof token === "string" ? token.trim() : "";

      if (!normalizedPendingId && !normalizedToken) {
        return null;
      }

      if (normalizedPendingId) {
        const cachedSnapshot = pendingSnapshotsRef.current.get(
          normalizedPendingId
        );
        if (cachedSnapshot) {
          hydrateBookingState(cachedSnapshot.booking || {});
          hydrateAvailabilityState(cachedSnapshot.availability || {});
          storePendingBookingContext({
            pendingBookingId: normalizedPendingId,
            booking: cachedSnapshot.booking || {},
            availability: cachedSnapshot.availability || {},
          });
          fetchedPendingIdsRef.current.add(normalizedPendingId);
          return {
            pendingBookingId: normalizedPendingId,
            booking: cachedSnapshot.booking || {},
            availability: cachedSnapshot.availability || {},
          };
        }
      }

      const cacheKeys = [];
      if (normalizedPendingId) {
        cacheKeys.push(`id:${normalizedPendingId}`);
      }
      if (normalizedToken) {
        cacheKeys.push(`token:${normalizedToken}`);
      }

      for (const key of cacheKeys) {
        if (pendingResolutionCacheRef.current.has(key)) {
          const cachedResolution = pendingResolutionCacheRef.current.get(key);
          if (!cachedResolution) {
            return null;
          }

          const effectivePendingId =
            cachedResolution.pendingBookingId || normalizedPendingId;
          const bookingPayload = cachedResolution.booking || {};
          const availabilityPayload = cachedResolution.availability || {};

          hydrateBookingState(bookingPayload);
          hydrateAvailabilityState(availabilityPayload);
          storePendingBookingContext({
            pendingBookingId: effectivePendingId,
            booking: bookingPayload,
            availability: availabilityPayload,
          });

          pendingSnapshotsRef.current.set(effectivePendingId, {
            booking: bookingPayload,
            availability: availabilityPayload,
          });
          fetchedPendingIdsRef.current.add(effectivePendingId);

          return {
            pendingBookingId: effectivePendingId,
            booking: bookingPayload,
            availability: availabilityPayload,
          };
        }
      }

      try {
        const response = await axios.post(
          "https://us-central1-yavola-8ca8d.cloudfunctions.net/resolvePendingBooking",
          {
            pendingBookingId: normalizedPendingId || undefined,
            token: normalizedToken || undefined,
          }
        );

        const data = response?.data;

        if (!data?.success || !data?.pendingBookingId) {
          cacheKeys.forEach((key) =>
            pendingResolutionCacheRef.current.set(key, null)
          );
          return null;
        }

        const resolution = {
          pendingBookingId: data.pendingBookingId,
          booking: data.booking || {},
          availability: data.availability || {},
        };

        const cacheAliases = new Set([
          `id:${data.pendingBookingId}`,
          ...cacheKeys,
        ]);

        cacheAliases.forEach((key) => {
          pendingResolutionCacheRef.current.set(key, resolution);
        });

        if (normalizedToken) {
          tokenLookupCacheRef.current.set(
            normalizedToken,
            data.pendingBookingId
          );
        }

        pendingSnapshotsRef.current.set(data.pendingBookingId, {
          booking: resolution.booking,
          availability: resolution.availability,
        });
        fetchedPendingIdsRef.current.add(data.pendingBookingId);

        if (
          normalizedPendingId &&
          normalizedPendingId !== data.pendingBookingId
        ) {
          pendingSnapshotsRef.current.set(normalizedPendingId, {
            booking: resolution.booking,
            availability: resolution.availability,
          });
          fetchedPendingIdsRef.current.add(normalizedPendingId);
        }

        hydrateBookingState(resolution.booking);
        hydrateAvailabilityState(resolution.availability);
        storePendingBookingContext({
          pendingBookingId: data.pendingBookingId,
          booking: resolution.booking,
          availability: resolution.availability,
        });

        return resolution;
      } catch (error) {
        console.error("Failed to resolve pending booking details", error);
        cacheKeys.forEach((key) =>
          pendingResolutionCacheRef.current.set(key, null)
        );
        return null;
      }
    },
    [hydrateAvailabilityState, hydrateBookingState]
  );

  const buildUserDetails = useCallback(
    (bookingOverride, availabilityOverride) => {
      const bookingSource = bookingOverride || bookingDetails || {};
      const availabilitySource = availabilityOverride || carAvailabilityDetails || {};

      return {
        email: bookingSource.email || "",
        cellphone: bookingSource.cellphone || "",
        country: bookingSource.country || "",
        date: availabilitySource.date || "",
        session: availabilitySource.session || "",
        people: Number(availabilitySource.people) || 0,
        name: bookingSource.name || "",
        totalPrice: Number(bookingSource.totalPrice) || 0,
        pickupPoint: availabilitySource.pickupPoint || "",
      };
    },
    [bookingDetails, carAvailabilityDetails]
  );

  const getMissingFields = useCallback((details) => {
    const missing = [];
    if (!details.email) missing.push("email");
    if (!details.name) missing.push("name");
    if (!details.date) missing.push("date");
    if (!details.session) missing.push("session");
    const hasValidPeople = Number.isFinite(details.people) && details.people > 0;
    if (!hasValidPeople) missing.push("people");
    const hasValidTotalPrice =
      Number.isFinite(details.totalPrice) && details.totalPrice >= 0;
    if (!hasValidTotalPrice) missing.push("total price");
    return missing;
  }, []);

  const lookupPendingBookingIdByToken = useCallback(async (token) => {
    if (token === undefined || token === null) {
      return null;
    }

    const lookupKey = String(token);
    const cachedValue = tokenLookupCacheRef.current.get(lookupKey);
    if (cachedValue !== undefined) {
      return cachedValue;
    }

    const sanitizedToken = lookupKey.trim();
    if (!sanitizedToken) {
      tokenLookupCacheRef.current.set(lookupKey, null);
      return null;
    }

    try {
      const db = getFirestore();
      const tokenRef = doc(db, "pendingBookingTokens", sanitizedToken);
      const tokenSnapshot = await getDoc(tokenRef);

      if (!tokenSnapshot.exists()) {
        tokenLookupCacheRef.current.set(lookupKey, null);
        return null;
      }

      const pendingId = tokenSnapshot.data()?.pendingBookingId;
      const normalizedPendingId =
        typeof pendingId === "string" ? pendingId.trim() : null;

      if (normalizedPendingId) {
        tokenLookupCacheRef.current.set(lookupKey, normalizedPendingId);
        return normalizedPendingId;
      }

      tokenLookupCacheRef.current.set(lookupKey, null);
      return null;
    } catch (error) {
      console.error("Failed to lookup pending booking identifier by token", error);
      tokenLookupCacheRef.current.set(lookupKey, null);
      return null;
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryPendingId =
      params.get("pendingBookingId") ||
      params.get("CompanyRef") ||
      params.get("companyRef") ||
      params.get("pending_booking_id") ||
      params.get("pendingBookingID") ||
      params.get("pendingbookingid");

    if (typeof queryPendingId === "string") {
      const normalized = queryPendingId.trim();
      if (normalized.length > 0 && normalized !== pendingBookingId) {
        setPendingBookingId(normalized);
      }
    }
  }, [location.search, pendingBookingId]);

  useEffect(() => {
    const cachedContext = readPendingBookingContext();

    if (!cachedContext || !cachedContext.pendingBookingId) {
      return;
    }

    const { pendingBookingId: cachedPendingId } = cachedContext;
    const cachedBooking = cachedContext.booking || {};
    const cachedAvailability = cachedContext.availability || {};

    pendingSnapshotsRef.current.set(cachedPendingId, {
      booking: cachedBooking,
      availability: cachedAvailability,
    });

    if (!pendingBookingId) {
      setPendingBookingId(cachedPendingId);
    }

    if (!pendingBookingId || pendingBookingId === cachedPendingId) {
      hydrateBookingState(cachedBooking);
      hydrateAvailabilityState(cachedAvailability);
    }
  }, [
    pendingBookingId,
    hydrateBookingState,
    hydrateAvailabilityState,
  ]);

  useEffect(() => {
    if (!pendingBookingId) {
      return;
    }

    if (fetchedPendingIdsRef.current.has(pendingBookingId)) {
      return;
    }

    const hydratePendingBooking = async () => {
      if (!paymentStatus) {
        updateStatus("Restoring booking details. Please wait...");
      }

      try {
        await fetchAndHydratePendingBooking(
          pendingBookingId,
          getLastTransactionToken()
        );
      } catch (error) {
        console.error("Failed to hydrate pending booking", error);
      }
    };

    hydratePendingBooking();
  }, [
    pendingBookingId,
    fetchAndHydratePendingBooking,
    paymentStatus,
    updateStatus,
    getLastTransactionToken,
  ]);

  const userDetails = useMemo(() => buildUserDetails(), [buildUserDetails]);

  const finalizePendingBookingRecord = useCallback(
    async ({
      pendingId,
      bookingId,
      paymentMetadata,
      bookingPayload,
      availabilityPayload,
    }) => {
      if (!pendingId) {
        return;
      }

      const db = getFirestore();

      try {
        const pendingRef = doc(db, "pendingBookings", pendingId);
        const existingSnapshot = pendingSnapshotsRef.current.get(pendingId);
        const updatedSnapshot = {
          booking: bookingPayload || existingSnapshot?.booking,
          availability: availabilityPayload || existingSnapshot?.availability,
        };

        const updatePayload = {
          status: "confirmed",
          bookingId: bookingId || null,
          finalizedAt: serverTimestamp(),
          payment: {
            status: "paid",
            metadata: paymentMetadata,
          },
        };

        if (updatedSnapshot.booking) {
          updatePayload.booking = updatedSnapshot.booking;
        }

        if (updatedSnapshot.availability) {
          updatePayload.availability = updatedSnapshot.availability;
        }

        await updateDoc(pendingRef, updatePayload);
        pendingSnapshotsRef.current.set(pendingId, updatedSnapshot);
        fetchedPendingIdsRef.current.add(pendingId);
      } catch (error) {
        console.error("Failed to finalize pending booking record", error);
      }

      try {
        const tokensQuery = query(
          collection(db, "pendingBookingTokens"),
          where("pendingBookingId", "==", pendingId)
        );
        const tokensSnapshot = await getDocs(tokensQuery);
        if (!tokensSnapshot.empty) {
          await Promise.all(
            tokensSnapshot.docs.map((tokenDoc) => deleteDoc(tokenDoc.ref))
          );
        }
      } catch (error) {
        console.error("Failed to delete pending booking token lookup", error);
      }
    },
    []
  );

  const handleSuccessfulPayment = useCallback(
    async ({
      verificationPayload,
      gatewayParams,
      pendingBookingIdentifier,
      transactionToken,
    }) => {
      if (bookingFinalizedRef.current) {
        return;
      }

      const paymentReference =
        verificationPayload?.transactionId ||
        verificationPayload?.transactionToken ||
        verificationPayload?.token ||
        gatewayParams?.reference ||
        gatewayParams?.TransID ||
        gatewayParams?.transID ||
        gatewayParams?.transId ||
        null;

      const supportSuffix = paymentReference
        ? ` Please contact support with reference ${paymentReference}.`
        : " Please contact support.";

      let effectiveUserDetails = userDetails;
      let missingFields = getMissingFields(effectiveUserDetails);
      const fallbackToken =
        transactionToken ||
        verificationPayload?.transactionToken ||
        verificationPayload?.token ||
        null;

      if (missingFields.length > 0) {
        const fallbackData = await fetchAndHydratePendingBooking(
          pendingBookingIdentifier,
          fallbackToken
        );

        if (fallbackData) {
          effectiveUserDetails = buildUserDetails(
            fallbackData.booking,
            fallbackData.availability
          );
          missingFields = getMissingFields(effectiveUserDetails);
          storePendingBookingContext({
            pendingBookingId: pendingBookingIdentifier,
            booking: fallbackData.booking,
            availability: fallbackData.availability,
          });
        }
      }

      if (missingFields.length > 0) {
        setIsLoading(false);
        updateStatus(
          "Payment verified! Yavola will confirm your booking soon.",
          "success"
        );
        sendPaymentIssueEmail({
          subject: "Payment verified but booking details incomplete",
          reason: `Missing booking fields: ${missingFields.join(", ")}`,
          details: {
            pendingBookingId: pendingBookingIdentifier,
            userDetails: effectiveUserDetails,
          },
          pendingBookingId: pendingBookingIdentifier,
        });
        return;
      }

      bookingFinalizedRef.current = true;
      updateStatus("Payment verified. Finalizing your booking...", "info");

      try {
        const availabilityResult = await dispatch(checkAvailability()).unwrap();

        if (availabilityResult?.status !== "success") {
          throw new Error("Unable to reserve availability. Please contact support.");
        }

        const paymentMetadata = {
          ...verificationPayload,
          gatewayParams,
          verifiedAt: new Date().toISOString(),
        };

        const bookingPayload = {
          ...effectiveUserDetails,
          payment: {
            status: "paid",
            metadata: paymentMetadata,
          },
        };

        const availabilityPayload = {
          date: effectiveUserDetails.date,
          session: effectiveUserDetails.session,
          people: effectiveUserDetails.people,
          pickupPoint: effectiveUserDetails.pickupPoint,
        };

        const savedBooking = await dispatch(saveBooking(bookingPayload)).unwrap();
        await finalizePendingBookingRecord({
          pendingId: pendingBookingIdentifier,
          bookingId: savedBooking?.id,
          paymentMetadata,
          bookingPayload,
          availabilityPayload,
        });

        clearPendingBookingContext();

        //get firestore instance
        const db = getFirestore();

        const documentDetails = {
          guestName: effectiveUserDetails.name,
          bookingDateIso: effectiveUserDetails.date,
          pickupPointDisplay: effectiveUserDetails.pickupPoint,
          totalPriceValue: effectiveUserDetails.totalPrice,
        };

        const {
          html: customerHtml,
          text: customerText,
          normalized: normalizedDetails,
        } = buildBookingConfirmationEmailContent(documentDetails);

        const emailData = {
          to: effectiveUserDetails.email,
          message: {
            subject: "Booking Confirmation - Yavola Tours",
            html: customerHtml,
            text: customerText,
          },
        };

        if (emailData.to) {
          try {
            const pdfAttachment = await createBookingConfirmationPdf(
              documentDetails
            );

            const base64Content =
              typeof pdfAttachment?.base64 === "string"
                ? pdfAttachment.base64.trim()
                : "";

            if (base64Content) {
              const attachments = [
                {
                  filename: pdfAttachment.fileName,
                  content: base64Content,
                  type: "application/pdf",
                  disposition: "attachment",
                  encoding: "base64",
                },
              ];

              emailData.message.attachments = attachments;
              emailData.attachments = attachments;
            }
          } catch (pdfError) {
            console.error(
              "Failed to generate booking confirmation PDF for payment success email",
              pdfError
            );
          }
        }

        // Construct email details for business owner
        const ownerEmailData = {
          to: "bookings@yavolatours.com",
          message: {
            subject: "New Booking - Yavola Tours",
            html: `<p>Hello Business Owner,</p>
                 <p>A new booking has been made by ${normalizedDetails.guestName} for ${normalizedDetails.bookingDateDisplay}.</p>
                 <p>Pickup Point: ${normalizedDetails.pickupPointDisplay}</p>
                 <p>Total Price: ${normalizedDetails.totalPriceDisplay}</p>`,
          },
        };

        try {
          const mailCollectionRef = collection(db, "mail");
          const docRef = await addDoc(mailCollectionRef, emailData);
          console.log("email trigger document with ID:", docRef.id);

          const ownerDocRef = await addDoc(
            collection(db, "mail"),
            ownerEmailData
          );
          console.log(
            "Business owner email trigger document with ID:",
            ownerDocRef.id
          );
        } catch (error) {
          console.error("error triggering email", error);
        }

        updateStatus(
          "Payment was successful! Booking confirmed. Redirecting to home...",
          "success"
        );
        setIsLoading(false);
        setTimeout(() => {
          nav("/", { replace: true });
        }, 2500);
      } catch (error) {
        bookingFinalizedRef.current = false;
        console.error("Booking finalization failed", error);
        const failureMessage = error?.message
          ? `Payment verified, but booking failed: ${error.message}.`
          : "Payment verified, but booking failed.";
        updateStatus(`${failureMessage}${supportSuffix}`, "error");
        setIsLoading(false);
        sendPaymentIssueEmail({
          subject: "Booking finalization failed after payment",
          reason: error?.message || "Unknown booking finalization error.",
          details: {
            pendingBookingId: pendingBookingIdentifier,
            verificationPayload,
            gatewayParams,
            error: {
              message: error?.message,
              stack: error?.stack,
            },
          },
          pendingBookingId: pendingBookingIdentifier,
        });
      }
    },
    [
      dispatch,
      nav,
      finalizePendingBookingRecord,
      userDetails,
      fetchAndHydratePendingBooking,
      buildUserDetails,
      getMissingFields,
      updateStatus,
    ]
  );

  const getTransactionDetailsFromLocation = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const allParams = Object.fromEntries(params.entries());
    const possibleKeys = [
      "TransactionToken",
      "transactionToken",
      "token",
      "reference",
      "TransID",
      "transID",
      "transId",
    ];

    for (const key of possibleKeys) {
      const value = allParams[key];
      if (value) {
        if (typeof value === "string") {
          const trimmedValue = value.trim();
          const resolvedToken = trimmedValue || value;
          setLastTransactionToken(resolvedToken);
          return { token: resolvedToken, params: allParams };
        }

        const coercedValue = String(value);
        setLastTransactionToken(coercedValue);
        return { token: coercedValue, params: allParams };
      }
    }

    resetLastTransactionToken();
    return { token: null, params: allParams };
  }, [location.search, resetLastTransactionToken, setLastTransactionToken]);

  const buildVerificationPayload = useCallback(
    (token, params, fallbackPendingId) => {
      const sanitize = (value) =>
        typeof value === "string" && value.trim().length > 0 ? value : undefined;

      const payload = { token };

      const pendingIdCandidate =
        params.pendingBookingId ||
        params.CompanyRef ||
        params.companyRef ||
        params.pending_booking_id ||
        params.pendingBookingID ||
        params.pendingbookingid ||
        fallbackPendingId;

      const sanitizedPendingId = sanitize(pendingIdCandidate);
      if (sanitizedPendingId) {
        payload.pendingBookingId = sanitizedPendingId;
      }

      const additionalFields = {
        transactionToken: params.TransactionToken ?? params.transactionToken,
        transactionId: params.TransID ?? params.transID ?? params.transId,
        approvalCode:
          params.CCDapproval ?? params.ccdApproval ?? params.ccdapproval,
        bookingReference: params.PnrID ?? params.pnrID ?? params.pnrId,
        companyReference: params.CompanyRef ?? params.companyRef,
      };

      Object.entries(additionalFields).forEach(([key, value]) => {
        const sanitizedValue = sanitize(value);
        if (sanitizedValue) {
          payload[key] = sanitizedValue;
        }
      });

      return payload;
    },
    []
  );

  const extractPendingBookingId = useCallback((payload) => {
    if (!payload || typeof payload !== "object") {
      return undefined;
    }

    const targetKey = "pendingbookingid";

    for (const [key, value] of Object.entries(payload)) {
      if (
        typeof key === "string" &&
        key.toLowerCase().replace(/[_-]/g, "") === targetKey
      ) {
        if (typeof value === "string" || typeof value === "number") {
          const trimmedValue = String(value).trim();
          if (trimmedValue.length > 0) {
            return trimmedValue;
          }
        }
      }
    }

    return undefined;
  }, []);

  useEffect(() => {
    const verifyPayment = async (
      transactionToken,
      params,
      retryCount = 0
    ) => {
      setIsLoading(true);
      updateStatus("Verifying payment with the gateway...", "info");


      const currentPendingId =
        typeof pendingBookingId === "string" && pendingBookingId.trim().length > 0
          ? pendingBookingId.trim()
          : undefined;
      const cachedContext = readPendingBookingContext();
      const storedPendingId =
        currentPendingId || cachedContext?.pendingBookingId || undefined;

      try {
        const verificationPayload = buildVerificationPayload(
          transactionToken,
          params,
          storedPendingId
        );
        const initialPendingId =
          typeof verificationPayload.pendingBookingId === "string"
            ? verificationPayload.pendingBookingId.trim()
            : verificationPayload.pendingBookingId;

        const response = await axios.post(
          "https://us-central1-yavola-8ca8d.cloudfunctions.net/verifyTransaction",
          verificationPayload
        );

        const responsePendingId =
          extractPendingBookingId(response?.data) ||
          extractPendingBookingId(response?.data?.data);
        const effectivePendingId =
          initialPendingId || responsePendingId || storedPendingId;

        let normalizedEffectiveId;

        if (effectivePendingId !== undefined && effectivePendingId !== null) {
          const trimmedEffectiveId = String(effectivePendingId).trim();
          if (trimmedEffectiveId) {
            normalizedEffectiveId = trimmedEffectiveId;
            if (normalizedEffectiveId !== currentPendingId) {
              setPendingBookingId(normalizedEffectiveId);
            } else if (!fetchedPendingIdsRef.current.has(normalizedEffectiveId)) {
              await fetchAndHydratePendingBooking(
                normalizedEffectiveId,
                transactionToken
              );
            }
          }
        }

        if (!normalizedEffectiveId && transactionToken) {
          const recoveredPendingId = await lookupPendingBookingIdByToken(
            transactionToken
          );

          if (recoveredPendingId) {
            normalizedEffectiveId = recoveredPendingId;
            if (normalizedEffectiveId !== currentPendingId) {
              setPendingBookingId(normalizedEffectiveId);
            }

            if (!fetchedPendingIdsRef.current.has(normalizedEffectiveId)) {
              await fetchAndHydratePendingBooking(
                normalizedEffectiveId,
                transactionToken
              );
            }
          }
        }

        if (response.data.serverFinalized) {
          const confirmed = response.data.status === "confirmed";
          updateStatus(confirmed ? "Payment received and your booking is confirmed." :
            "Payment received. Our team is reviewing your booking.", confirmed ? "success" : "warning");
          setHasVerified(true);
          setIsLoading(false);
          return;
        }

        if (response.data.success) {
          if (!normalizedEffectiveId) {
            console.error(
              "Missing pending booking identifier after successful verification",
              {
                verificationPayload,
                verificationResponse: response?.data,
                storedPendingBookingId: storedPendingId,
                tokenLookupAttempted: Boolean(transactionToken),
              }
            );
            updateStatus(
              "Payment verified, but we couldn't locate your booking reference. Please contact support so we can finalize your booking.",
              "error"
            );
            setIsLoading(false);
            sendPaymentIssueEmail({
              subject: "Payment verified without pending booking reference",
              reason:
                "Gateway verification succeeded but no pending booking identifier was available.",
              details: {
                pendingBookingId:
                  normalizedEffectiveId || storedPendingId || effectivePendingId,
                verificationPayload,
                verificationResponse: response?.data,
                storedPendingBookingId: storedPendingId,
                tokenLookupAttempted: Boolean(transactionToken),
              },
              pendingBookingId:
                normalizedEffectiveId || storedPendingId || effectivePendingId,
            });
            return;
          }

          await handleSuccessfulPayment({
            verificationPayload,
            gatewayParams: params,
            pendingBookingIdentifier: normalizedEffectiveId,
            transactionToken,
          });
        } else {
          updateStatus(
            "Payment verification failed. Please contact support.",
            "error"
          );
          setIsLoading(false);
          sendPaymentIssueEmail({
            subject: "Payment verification failed",
            reason:
              response?.data?.message ||
              "Gateway verification response indicated failure.",
            details: {
              pendingBookingId:
                normalizedEffectiveId || effectivePendingId || storedPendingId,
              verificationPayload,
              verificationResponse: response?.data,
              storedPendingBookingId: storedPendingId,
            },
            pendingBookingId: normalizedEffectiveId || effectivePendingId,
          });
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status === 429) {
          // If a rate limit error is encountered, retry after a delay
          const maxRetries = 100;
          if (retryCount < maxRetries) {
            const retryDelay = 1000; // 1 second
            console.log(
              `Rate limit hit, retrying after ${retryDelay / 1000} seconds...`
            );
            setTimeout(() => {
              verifyPayment(transactionToken, params, retryCount + 1);
            }, retryDelay);
          } else {
            console.error("Max retries reached. Please try again later.");
            updateStatus("Service is busy. Please try again later.", "error");
            setIsLoading(false);
            sendPaymentIssueEmail({
              subject: "Payment verification rate limit exhausted",
              reason:
                "Exceeded retry attempts while verifying payment due to rate limiting.",
              details: {
                pendingBookingId: storedPendingId,
                transactionToken,
                params,
                retryCount: retryCount + 1,
              },
              pendingBookingId: storedPendingId,
            });
          }
        } else {
          // Handle other types of errors here
          console.error("Unexpected error during payment verification:", error);
          updateStatus(
            "An unexpected error occurred. Please contact support.",
            "error"
          );
          setIsLoading(false);
          sendPaymentIssueEmail({
            subject: "Unexpected error during payment verification",
            reason: error?.message || "Unknown verification error.",
            details: {
              transactionToken,
              params,
              pendingBookingId: storedPendingId,
              error: {
                message: error?.message,
                stack: error?.stack,
              },
            },
            pendingBookingId: storedPendingId,
          });
        }
      }
    };

    if (!hasVerified) {
      const { token: transactionToken, params } = getTransactionDetailsFromLocation();
      if (transactionToken) {
        setHasVerified(true);
        verifyPayment(transactionToken, params);
      } else {
        updateStatus("Invalid transaction. No token provided.", "error");
        setIsLoading(false);
      }
    }
  }, [
    location,
    dispatch,
    nav,
    userDetails,
    hasVerified,
    handleSuccessfulPayment,
    buildVerificationPayload,
    getTransactionDetailsFromLocation,
    pendingBookingId,
    extractPendingBookingId,
    fetchAndHydratePendingBooking,
    updateStatus,
    lookupPendingBookingIdByToken,
  ]);

  return (
    <div className="payment-success-container">
      <h1>Payment Status</h1>
      {isLoading ? (
        <ClipLoader size={50} color={"#1C611C"} loading={isLoading} />
      ) : paymentStatus ? (
        <p className={`status-message status-${statusTone}`}>
          {paymentStatus}
        </p>
      ) : null}
    </div>
  );
}

export default PaymentSuccess;
